import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { readStoredUser } from "../../utils/sessionUser";
import CorporatePicker from "./CorporatePicker";
import { PURCHASING_TABLES, purchasingTableName } from "./viewDbPurchasingTables";
import { canReadAdminTable, canDeleteAdminTable, isPurchasingAdminTable, isScopedStockAdminTable } from "./viewDbPurchasingAccess";

const PAGED_OCR_TABLES = new Set(["delivery_note_ocr_imports", "delivery_note_ocr_logs"]);

function ViewDB() {
  const [activeTab, setActiveTab] = useState("pms"); // "pms" o "purchasing"
  const [selectedTable, setSelectedTable] = useState("reservations");
  const [storedData, setData] = useState([]);
  const [cols, setCols] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deleteIds, setDeleteIds] = useState("");
  const [recordIdInput, setRecordIdInput] = useState("");
  const [lookupError, setLookupError] = useState("");
  const [ocrQuery, setOcrQuery] = useState({ recordId: null, beforeId: null, revision: 0 });
  const [nextBeforeId, setNextBeforeId] = useState(null);
  const [, updateSession] = useState(0);
  const [loadedScope, setLoadedScope] = useState("");
  const actorId = useRef(readStoredUser()?.id);
  const context = useRef({ signature: "", epoch: 0 });
  const mounted = useRef(true);
  const requestVersion = useRef(0);
  const mutationLock = useRef(false);
  const [deleting, setDeleting] = useState(false);
  const capture = useCallback(() => {
    const user = readStoredUser();
    const signature = JSON.stringify([user?.id, user?.active_business_unit_id, user?.permissions, localStorage.getItem("palantir_token")]);
    if (context.current.signature !== signature) context.current = { signature, epoch: context.current.epoch + 1 };
    return { ...context.current, user };
  }, []);
  const current = useCallback((stamp, table, action = "read") => {
    const actual = capture();
    return mounted.current && String(actual.user?.id) === String(actorId.current) &&
      actual.signature === stamp.signature && actual.epoch === stamp.epoch &&
      (action === "delete" ? canDeleteAdminTable(actual.user, table) : canReadAdminTable(actual.user, table));
  }, [capture]);
  const session = capture();
  const renderedSession = useRef(session);
  renderedSession.current = session;
  const contextKey = `${session.signature}:${session.epoch}`;
  const isPagedOcr = PAGED_OCR_TABLES.has(selectedTable);
  const ocrIdLabel = selectedTable === "delivery_note_ocr_imports" ? "ID de importación" : "ID de registro";
  const scope = `${contextKey}:${selectedTable}:${isPagedOcr ? JSON.stringify(ocrQuery) : "raw"}`;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const canReadTable = current(session, selectedTable);
  const canDeleteTable = current(session, selectedTable, "delete");
  const data = loadedScope === scope && canReadTable ? storedData : [];
  const requestConfig = useCallback((stamp, table) => {
    const token = localStorage.getItem("palantir_token");
    if (!token) throw new Error("Debes iniciar sesión.");
    const config = { headers: { Authorization: `Bearer ${token}` } };
    if (isScopedStockAdminTable(table)) {
      const bu = Number(stamp.user?.active_business_unit_id);
      if (!Number.isSafeInteger(bu) || bu <= 0) throw new Error("Selecciona un hotel activo.");
      config.headers["X-Pilcore-Business-Unit-Id"] = String(bu);
      config.params = { business_unit_id: bu };
      config.pilcoreExpectedUserId = stamp.user.id;
    }
    return config;
  }, []);

  // ✅ JSON viewer popup (centered, like your other popups)
  const [jsonModal, setJsonModal] = useState({
    isOpen: false,
    title: "",
    text: "",
  });
  useEffect(() => {
    mounted.current = true;
    const invalidate = (event) => {
      if (event.type === "storage" && event.key && !["palantir_user", "palantir_token"].includes(event.key)) return;
      const previous = renderedSession.current;
      const next = capture();
      if (previous.signature === next.signature && previous.epoch === next.epoch) return;
      requestVersion.current += 1;
      setData([]); setCols([]); setDeleteIds(""); setError("");
      setLoadedScope(""); setNextBeforeId(null); setRecordIdInput(""); setLookupError("");
      setOcrQuery((query) => ({ recordId: null, beforeId: null, revision: query.revision + 1 }));
      setJsonModal({ isOpen: false, title: "", text: "" });
      updateSession((value) => value + 1);
    };
    window.addEventListener("palantir-session-updated", invalidate);
    window.addEventListener("storage", invalidate);
    return () => {
      mounted.current = false; requestVersion.current += 1;
      window.removeEventListener("palantir-session-updated", invalidate);
      window.removeEventListener("storage", invalidate);
    };
  }, [capture]);

  const openJsonModal = (title, value) => {
    if (!current(session, selectedTable) || loadedScope !== scopeRef.current) return;
    let text = "";
    try {
      text = JSON.stringify(value, null, 2);
    } catch (e) {
      console.warn("🟨 [ViewDB][JSON] stringify failed:", e);
      text = String(value);
    }

    console.log("🟦 [ViewDB][JSON] open:", { title, length: text.length });
    setJsonModal({ isOpen: true, title: title || "JSON", text, scope });
  };

  const closeJsonModal = () => {
    console.log("🟦 [ViewDB][JSON] close");
    setJsonModal((prev) => ({ ...prev, isOpen: false }));
  };

  const copyJsonToClipboard = async () => {
    if (!current(session, selectedTable) || jsonModal.scope !== scopeRef.current) return;
    const text = String(jsonModal.text || "");
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      console.log("🟩 [ViewDB][JSON] copied:", text.length);
      alert("Copied to clipboard.");
    } catch (e) {
      console.error("❌ [ViewDB][JSON] copy failed:", e);
      alert("Copy failed.");
    }
  };


  // 🧩 Generador de columnas
  const buildColumns = useCallback((rows) => {
    const colSet = new Set();
    for (const row of rows) Object.keys(row || {}).forEach((k) => colSet.add(k));
    const ordered = Array.from(colSet);
    ordered.sort((a, b) => {
      const rank = (k) =>
        k === "id" ? -3 : k.toLowerCase() === "createdat" ? 2 : k.toLowerCase() === "updatedat" ? 3 : 0;
      return rank(a) - rank(b) || a.localeCompare(b);
    });
    return ordered;
  }, []);

  // 🧩 Función para borrar registros por IDs
  const handleDelete = async () => {
    if (!current(session, selectedTable, "delete") || loadedScope !== scopeRef.current) return;
    const stamp = capture(), table = selectedTable, selectedScope = scope;
    if (mutationLock.current || !current(stamp, table, "delete") || !deleteIds.trim()) return;
    if (!window.confirm(`¿Seguro que quieres borrar estos IDs de ${selectedTable}?`)) return;
    if (!current(stamp, table, "delete")) return;

    try {
      // Parsear entrada (admite "1,2,5-10")
      const idsArray = [];
      deleteIds.split(",").forEach((part) => {
        const trimmed = part.trim();
        if (!trimmed) return;

        if (trimmed.includes("-")) {
          const [start, end] = trimmed.split("-").map((n) => parseInt(n.trim(), 10));
          if (!isNaN(start) && !isNaN(end) && end >= start) {
            for (let i = start; i <= end; i++) idsArray.push(i);
          }
        } else {
          const num = parseInt(trimmed, 10);
          if (!isNaN(num)) idsArray.push(num);
        }
      });

      if (!idsArray.length) {
        alert("⚠️ No se detectaron IDs válidos.");
        return;
      }

      const base = process.env.REACT_APP_API_URL;
      const url = `${base}/api/admin/delete/${selectedTable}`;
      mutationLock.current = true; setDeleting(true);
      await axios.delete(url, {
        ...requestConfig(stamp, table),
        data: { ids: idsArray },
      });
      if (!current(stamp, table, "delete") || scopeRef.current !== selectedScope) return;
      alert(`✅ Borrados ${idsArray.length} registros de ${selectedTable}`);
      setDeleteIds("");
      await fetchRaw();
    } catch (err) {
      if (!current(stamp, table, "delete") || scopeRef.current !== selectedScope) return;
      if (err.response && err.response.status === 409) {
        alert(
          `⚠️ No se pudo borrar de ${selectedTable}.\n\n` +
            `Detalle: ${err.response.data.detail || "Tienen registros hijos."}\n\n` +
            `Constraint: ${err.response.data.constraint}`
        );
      } else {
        console.error("❌ Error borrando:", err);
        alert("Error al borrar registros.");
      }
    } finally { mutationLock.current = false; if (mounted.current) setDeleting(false); }
  };


  // 🔍 Mapas de tablas por módulo
  const PMS_TABLES = {
    amenities: "Amenities",
    booking_engine_assets: "Booking Engine Assets",
    booking_engine_profiles: "Booking Engine Profiles",
    booking_engine_room_categories: "Booking Engine Room Categories",
    booking_public_sessions: "Booking Public Sessions",
    booking_public_tokens: "Booking Public Tokens",
    booking_terms_acceptances: "Booking Terms Acceptances",
    business_units: "Business Units",
    channel_manager_credentials: "Channel Manager Credentials",
    city_events: "City Events",
    client_category_allocation: "Client Category Allocation",
    clients: "Clients",
    competitor_room_type_mappings: "Competitor Room Type Mappings",
    cron_job_runs: "Cron Job Runs",
    cron_job_scopes: "Cron Job Scopes",
    cron_jobs: "Cron Jobs",
    customers: "Customers",
    daily_category_availability: "Daily Category Availability",
    dedicated_proxy_usage: "Dedicated Proxy Usage",
    extras: "Extras",
    forecast_occupancy: "Forecast Occupancy",
    hotel_settings: "Hotel Settings",
    income_generators: "Income Generators",
    lighthouse_outbox: "Lighthouse Outbox",
    lighthouse_room_rate_mappings: "Lighthouse Room Rate Mappings",
    lighthouse_service_mappings: "Lighthouse Service Mappings",
    market_rate_prices: "Market Rate Prices",
    password_reset_tokens: "Password Reset Tokens",
    payments: "Payments",
    rate_formula_execution_order: "Rate Formula Execution Order",
    rate_formula_inputs: "Rate Formula Inputs",
    rate_formulas: "Rate Formulas",
    rate_prices: "Rate Prices",
    rate_prices_history: "Rate Prices History",
    ratemetrics_competitor_prices_current: "RateMetrics Competitor Prices Current",
    ratemetrics_hook_rates: "RateMetrics Hook Rates",
    ratemetrics_hook_raw: "RateMetrics Hook Raw",
    ratemetrics_rateshop_configs: "RateMetrics Rateshop Configs",
    reservation_draft_category_availability: "Reservation Draft Category Availability",
    reservation_draft_category_availability_self: "Reservation Draft Category Availability (Self)",
    reservation_draft_capacity_flags: "Reservation Draft Capacity Flags",
    reservation_draft_extras: "Reservation Draft Extras",
    reservation_draft_extras_nightly_prices: "Reservation Draft Extras Nightly Prices",
    reservation_draft_group_rates: "Reservation Draft Group Rates",
    reservation_draft_guests: "Reservation Draft Guests",
    reservation_draft_nightly_price_overrides: "Reservation Draft Nightly Price Overrides",
    reservation_draft_nightly_prices: "Reservation Draft Nightly Prices",
    reservation_draft_room_assignments: "Room Assignments Draft Reservations",
    reservation_draft_room_category_amenities: "Room Category Amenities", // ❌ NO (ver abajo)
    reservation_drafts: "Reservation Drafts",
    reservation_extras: "Reservation Extras",
    reservation_extras_nightly_prices: "Reservation Extras Nightly Prices",
    reservation_group_drafts: "Reservation Group Drafts",
    reservation_group_payment_events: "Reservation Group Payment Events",
    reservation_group_payment_intents: "Reservation Group Payment Intents",
    reservation_group_payment_methods: "Reservation Group Payment Methods",
    reservation_group_payments: "Reservation Group Payments",
    reservation_group_rates: "Reservation Group Rates",
    reservation_groups: "Reservations Groups",
    reservation_guests: "Reservation Guests",
    reservation_history: "Reservation History",
    reservation_nightly_price_overrides: "Reservation Nightly Price Overrides",
    reservation_nightly_prices: "Reservation Nightly Prices",
    reservation_stay_nights: "Reservation Stay Nights",
    reservations: "Reservations",
    revenue_daily: "Revenue Daily",
    room_assignments: "Room Assignments",
    room_blocks: "Room Blocks",
    room_categories: "Room Categories",
    room_category_amenities: "Room Category Amenities",
    room_rates: "Room Rates",
    room_rates_history: "Room Rates History",
    room_status_changes: "Room Status Changes",
    rooms: "Rooms",
    roles: "Roles",
    role_business_units: "Role Business Units",
    scraper_cycle_state: "Scraper Cycle State",
    scraper_targets: "Scraper Targets",
    sequelize_meta: "SequelizeMeta",
    stop_sells: "Stop Sells",
    tables: "Tables",
    users: "Users",
    xotelo_market_prices: "Xotelo Market Prices",
    xotelo_market_prices_history: "Xotelo Market Prices History",
  };




    // 🧩 Función reutilizable de carga
  const getSortedTableEntries = (tableMap) =>
    Object.entries(tableMap).sort(
      ([aKey, aLabel], [bKey, bLabel]) =>
        aLabel.localeCompare(bLabel, undefined, { sensitivity: "base" }) ||
        aKey.localeCompare(bKey, undefined, { sensitivity: "base" })
    );

  const fetchRaw = useCallback(async () => {
    const stamp = capture(), version = ++requestVersion.current;
    setError(""); setData([]); setCols([]); setLoadedScope("");
    setNextBeforeId(null); setJsonModal({ isOpen: false, title: "", text: "" });
    if (!current(stamp, selectedTable)) { setLoading(false); return; }
    setLoading(true);
    try {
      const base = process.env.REACT_APP_API_URL;
      if (!base) throw new Error("REACT_APP_API_URL no está definido.");
      const config = requestConfig(stamp, selectedTable);
      if (isPagedOcr && !ocrQuery.recordId) {
        config.params = { page_size: 25, ...(ocrQuery.beforeId ? { before_id: ocrQuery.beforeId } : {}) };
      }
      const detailPath = isPagedOcr && ocrQuery.recordId ? `/${ocrQuery.recordId}` : "";
      const resp = await axios.get(`${base}/api/admin/${selectedTable.replace(/_/g, "-")}${detailPath}`, config);
      if (!current(stamp, selectedTable) || requestVersion.current !== version || scopeRef.current !== scope) return;
      const rows = isPagedOcr
        ? (ocrQuery.recordId ? (resp.data && !Array.isArray(resp.data) ? [resp.data] : []) : (Array.isArray(resp.data?.rows) ? resp.data.rows : []))
        : (Array.isArray(resp.data) ? resp.data : []);
      const cursor = resp.data?.next_before_id;
      setNextBeforeId(isPagedOcr && !ocrQuery.recordId && Number.isSafeInteger(cursor) && cursor > 0 ? cursor : null);
      setCols(buildColumns(rows)); setData(rows); setLoadedScope(scope);
    } catch (err) {
      if (current(stamp, selectedTable) && requestVersion.current === version && scopeRef.current === scope) {
        setError(isPagedOcr && err?.response?.status === 502
          ? "No se ha podido completar la consulta OCR (502). Vuelve a intentarlo o consulta un único registro por su ID. Si continúa, revisa los logs de aplicación."
          : isPagedOcr && ocrQuery.recordId && err?.response?.status === 404
            ? `No se ha encontrado el registro con ID ${ocrQuery.recordId}.`
            : err?.response?.data?.error || err.message);
      }
    } finally {
      if (mounted.current && requestVersion.current === version && scopeRef.current === scope) setLoading(false);
    }
  }, [capture, current, selectedTable, scope, requestConfig, buildColumns, isPagedOcr, ocrQuery]);

  useEffect(() => { fetchRaw(); }, [fetchRaw]);

  const changeOcrQuery = (recordId = null, beforeId = null) => {
    if (!isPagedOcr || !current(session, selectedTable)) return;
    requestVersion.current += 1;
    scopeRef.current = "";
    setLoadedScope(""); setData([]); setCols([]); setNextBeforeId(null); setError(""); setLookupError("");
    setJsonModal({ isOpen: false, title: "", text: "" });
    setRecordIdInput(recordId ? String(recordId) : "");
    setOcrQuery((query) => ({ recordId, beforeId, revision: query.revision + 1 }));
  };

  const loadOcrRecord = (event) => {
    event.preventDefault();
    if (!current(session, selectedTable)) return;
    const text = recordIdInput.trim(), id = Number(text);
    if (!/^[1-9]\d*$/.test(text) || !Number.isSafeInteger(id)) {
      setLookupError("Introduce un ID entero positivo.");
      return;
    }
    changeOcrQuery(id);
  };

  const selectTable = (table) => {
    requestVersion.current += 1;
    scopeRef.current = "";
    setSelectedTable(table); setDeleteIds(""); setRecordIdInput(""); setLookupError("");
    setLoadedScope(""); setNextBeforeId(null); setJsonModal({ isOpen: false, title: "", text: "" });
    setOcrQuery((query) => ({ recordId: null, beforeId: null, revision: query.revision + 1 }));
  };

  // ---------------------------
  // Export helpers (CSV/Excel)
  // ---------------------------
  const toExportString = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "object") {
      try {
        // Compact on purpose (no newlines) to keep CSV/TSV structure stable.
        return JSON.stringify(value);
      } catch (e) {
        console.warn("🟨 [ViewDB][Export] stringify failed:", e);
        return String(value);
      }
    }
    return String(value);
  };

    // Render de celda
    const renderCell = (value) => {
      if (value === null || value === undefined) return <span style={{ color: "#888" }}>null</span>;

      if (typeof value === "object") {
        let s = "";
        try {
          s = JSON.stringify(value);
        } catch {
          s = String(value);
        }
        return s.length > 200 ? `${s.slice(0, 200)}…` : s;
      }

      const s = String(value);
      return s.length > 200 ? `${s.slice(0, 200)}…` : s;
    };



  // Export CSV
  const exportToCSV = () => {
    if (!current(session, selectedTable) || loadedScope !== scopeRef.current) return;
    if (!data.length) return alert("No data to export.");

    const header = cols.join(",");
    const rows = data.map((r) =>
      cols
        .map((c) => {
          const v = toExportString(r?.[c]);
          return `"${v.replace(/"/g, '""')}"`
        })
        .join(",")
    );

    const csvContent = [header, ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${selectedTable}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 💾 Export Excel (tsv)
  const exportToExcel = () => {
    if (!current(session, selectedTable) || loadedScope !== scopeRef.current) return;
    if (!data.length) return alert("No data to export.");

    const quoteTsv = (value) => `"${toExportString(value).replace(/"/g, '""')}"`;
    const header = cols.map(quoteTsv).join("\t");
    const rows = data.map((r) =>
      cols
        .map((c) => quoteTsv(r?.[c]))
        .join("\t")
    );

    const blob = new Blob([header + "\n" + rows.join("\n")], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${selectedTable}.xls`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  const activeTableMap = activeTab === "pms" ? PMS_TABLES : PURCHASING_TABLES;
  const permittedEntries = (tableMap) => getSortedTableEntries(tableMap).filter(([table]) => current(session, table));
  const activeTableEntries = permittedEntries(activeTableMap);

  return (
    <div>
      {/* Tabs */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
        {["pms", "purchasing"].map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              const tableMap = tab === "pms" ? PMS_TABLES : PURCHASING_TABLES;
              const firstTable = permittedEntries(tableMap)[0]?.[0] || "";
              selectTable(firstTable);
            }}
            style={{
              padding: "8px 20px",
              borderRadius: "8px",
              border: "1px solid #444",
              background: activeTab === tab ? "#1de9b6" : "#222",
              color: activeTab === tab ? "#000" : "#fff",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            {tab === "pms" ? "PMS" : "Purchasing"}
          </button>
        ))}
      </div>

      

      <CorporatePicker key={`${activeTab}:${contextKey}`} label="Select table" value={canReadTable ? selectedTable : ""}
        options={activeTableEntries.map(([value, label]) => ({
          value, label, description: activeTab === "purchasing" ? purchasingTableName(value) : undefined,
        }))}
        onChange={selectTable}
        disabled={!activeTableEntries.length || deleting} />
      {!activeTableEntries.length && <p role="status">No tienes acceso a tablas de este módulo.</p>}
      {isPurchasingAdminTable(selectedTable) && !canDeleteTable && <p role="status">Gestiona estos registros desde su pantalla de Purchasing.</p>}

      {isPagedOcr && canReadTable && <section aria-label="Consulta OCR" style={{ marginTop: 16 }}>
        <p>La lista muestra hasta 25 resúmenes por página. Abre un registro para consultar su contenido completo.</p>
        <form onSubmit={loadOcrRecord} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <label htmlFor="ocr-record-id">{ocrIdLabel}</label>
          <input id="ocr-record-id" inputMode="numeric" value={recordIdInput}
            onChange={(event) => { setRecordIdInput(event.target.value); setLookupError(""); }} />
          <button type="submit" className="btn btn-sm btn-outline-info">Cargar registro</button>
          <button type="button" className="btn btn-sm btn-outline-light" onClick={() => changeOcrQuery()}>Volver a recientes</button>
          {!ocrQuery.recordId && <button type="button" className="btn btn-sm btn-outline-light"
            disabled={loading || loadedScope !== scope || !nextBeforeId}
            onClick={() => changeOcrQuery(null, nextBeforeId)}>Siguientes registros</button>}
        </form>
        {lookupError && <p role="alert">{lookupError}</p>}
        <p role="status">{ocrQuery.recordId ? `Detalle del registro ${ocrQuery.recordId}.` : ocrQuery.beforeId ? `Registros anteriores al ID ${ocrQuery.beforeId}.` : "Registros recientes."} Las exportaciones incluyen solo los registros cargados.</p>
      </section>}

      {/* 🔧 Panel de acciones: export + delete + refresh */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "10px" }}>
        {/* Export buttons */}
        <button
          className="btn btn-sm btn-outline-light"
          onClick={exportToCSV}
          disabled={!data.length}
        >
          ⬇️ CSV
        </button>
        <button
          className="btn btn-sm btn-outline-success"
          onClick={exportToExcel}
          disabled={!data.length}
        >
          ⬇️ Excel
        </button>

        {/* Delete + Refresh (compact) */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <input
            type="text"
            placeholder="IDs"
            disabled={!canDeleteTable || deleting || loadedScope !== scope}
            value={deleteIds}
            onChange={(e) => setDeleteIds(e.target.value)}
            style={{
              width: "90px",
              padding: "3px 6px",
              borderRadius: "6px",
              border: "1px solid #444",
              background: "#111",
              color: "#fff",
              fontSize: "13px",
            }}
          />
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={handleDelete}
            disabled={!canDeleteTable || deleting || loadedScope !== scope || !deleteIds.trim()}
            style={{ fontSize: "13px", padding: "3px 8px" }}
            title={canDeleteTable ? "Borrar registros por ID" : "Usa la pantalla de gestión correspondiente"}
          >
            🗑
          </button>

          {/* 🔄 Refresh button */}
          <button
            className="btn btn-sm btn-outline-info"
            onClick={fetchRaw} // ✅ ahora ejecuta el fetch directamente
            style={{ fontSize: "13px", padding: "3px 8px" }}
            title="Recargar tabla actual"
            disabled={loading || !canReadTable || deleting}
          >
            {loading ? "⏳" : "🔄"}
          </button>

        </div>
      </div>



      {/* Tabla */}
      <div style={{ marginTop: "20px" }}>
        {loading && <p>Cargando datos…</p>}
        {error && <p style={{ color: "tomato" }}>{error}</p>}
        {!loading && !error && data.length > 0 && (
          <div style={{ overflowX: "auto", maxHeight: "72vh", border: "1px solid #333", borderRadius: 8 }}>
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead>
                <tr>
                  {isPagedOcr && <th>Detalle</th>}
                  {cols.map((col) => (
                    <th
                      key={col}
                      style={{
                        borderBottom: "1px solid #444",
                        borderRight: "1px solid #333",
                        padding: "8px 10px",
                        background: "#0f0f0f",
                        color: "#1de9b6",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        textAlign: "left",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={row.id ?? i}>
                    {isPagedOcr && <td><button type="button" className="btn btn-sm btn-outline-info"
                      aria-label={ocrQuery.recordId ? `Ver JSON completo ${row.id}` : `Abrir detalle ${row.id}`}
                      onClick={() => ocrQuery.recordId ? openJsonModal(`Registro ${row.id}`, row) : changeOcrQuery(row.id)}>
                      {ocrQuery.recordId ? "Ver JSON completo" : "Abrir detalle"}
                    </button></td>}
                    {cols.map((col, j) => (
                      <td
                        key={j}
                        onClick={() => {
                          const v = row[col];
                          if (v && typeof v === "object") {
                            console.log("🟦 [ViewDB][JSON] cell click:", { col });
                            openJsonModal(col, v);
                          }
                        }}
                        style={{
                          borderBottom: "1px solid #262626",
                          borderRight: "1px solid #1a1a1a",
                          padding: "6px 10px",
                          background: i % 2 === 0 ? "#141414" : "#181818",
                          color: "#eaeaea",
                          fontSize: 14,
                          verticalAlign: "top",
                          cursor: row[col] && typeof row[col] === "object" ? "pointer" : "default",
                          whiteSpace: "nowrap",
                        }}
                        title={row[col] && typeof row[col] === "object" ? "Click to view JSON" : undefined}
                      >
                        {renderCell(row[col])}
                      </td>

                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && !error && data.length === 0 && <p style={{ color: "#aaa" }}>No hay datos en esta tabla.</p>}
      </div>

      {jsonModal.isOpen && jsonModal.scope === scope && canReadTable && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            padding: "18px",
          }}
          onClick={closeJsonModal}
        >
          <div
            style={{
              width: "92vw",
              maxWidth: "900px",
              background: "#121212",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: "10px",
              boxShadow: "0 18px 60px rgba(0,0,0,0.60)",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 text-light">
              <div className="d-flex justify-content-between align-items-center" style={{ gap: "12px" }}>
                <div style={{ fontSize: "1.0rem", fontWeight: 700 }}>
                  {jsonModal.title || "JSON"}
                </div>

                <div className="d-flex" style={{ gap: "10px" }}>
                  <button type="button" className="btn btn-sm btn-outline-light" onClick={copyJsonToClipboard}>
                    Copy
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-light" onClick={closeJsonModal}>
                    Close
                  </button>
                </div>
              </div>

              <pre
                className="mt-3 mb-0"
                style={{
                  maxHeight: "65vh",
                  overflow: "auto",
                  background: "#0f0f0f",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: "8px",
                  padding: "12px",
                  fontSize: "12px",
                  whiteSpace: "pre",
                  color: "rgba(255,255,255,0.92)",
                }}
              >
                {jsonModal.text}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );

}

export default ViewDB;
